import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  SignOut,
  Key,
  EnvelopeSimple,
  Phone,
  User,
  Calendar,
  Shield,
} from "@phosphor-icons/react";

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showConfirmLogout, setShowConfirmLogout] = useState(false);

  // Get user initials for avatar
  const getInitials = () => {
    if (!user || !user.fullName)
      return user?.email?.charAt(0)?.toUpperCase() || "?";
    return user.fullName
      .split(" ")
      .map((name) => name.charAt(0))
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  const handleLogout = () => {
    setShowConfirmLogout(true);
  };

  const confirmLogout = () => {
    setShowConfirmLogout(false);
    logout();
    navigate("/login");
  };

  const goToChangePassword = () => {
    navigate("/account/change-password");
  };

  const profileOptions = [
    {
      id: 1,
      title: "Change Password",
      icon: <Key size={20} />,
      action: goToChangePassword,
    },
    {
      id: 2,
      title: "Logout",
      icon: <SignOut size={20} />,
      action: handleLogout,
      className: "text-red-600",
    },
  ];

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="My Profile"
          subtitle="Manage your account information and settings"
        />

        <div className="max-w-4xl mx-auto px-6 py-8 md:py-12">
          {/* Profile Header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
            <div className="p-6 md:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                <div className="w-24 h-24 rounded-full bg-gradient-to-r from-[#133B6E]/10 to-[#003F75]/20 border-4 border-white shadow-sm flex items-center justify-center text-2xl font-bold text-[#133B6E]">
                  {getInitials()}
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {user?.fullName || "User"}
                  </h2>
                  <p className="text-gray-500 mt-1">
                    {user?.email || "No email"}
                  </p>
                  <div className="mt-3 inline-flex items-center gap-2 text-sm text-[#003F75] bg-blue-50 px-3 py-1 rounded-full">
                    <Shield size={16} weight="fill" />
                    <span>Verified Account</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
            <div className="p-6 md:p-8">
              <h3 className="text-lg font-medium text-gray-900 mb-5 flex items-center gap-2">
                <span className="text-[#133B6E]">
                  <User size={20} weight="duotone" />
                </span>
                Account Information
              </h3>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <p className="text-sm text-gray-500 mb-1">Full Name</p>
                  <div className="flex items-center gap-2">
                    <User size={16} className="text-[#133B6E]" />
                    <p className="font-medium text-gray-900">
                      {user?.fullName || "Not provided"}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <p className="text-sm text-gray-500 mb-1">Email Address</p>
                  <div className="flex items-center gap-2">
                    <EnvelopeSimple size={16} className="text-[#133B6E]" />
                    <p className="font-medium text-gray-900">
                      {user?.email || "Not provided"}
                    </p>
                  </div>
                </div>

                {user?.phone && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <p className="text-sm text-gray-500 mb-1">Phone Number</p>
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-[#133B6E]" />
                      <p className="font-medium text-gray-900">{user.phone}</p>
                    </div>
                  </div>
                )}

                {user?.createdAt && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <p className="text-sm text-gray-500 mb-1">Member Since</p>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-[#133B6E]" />
                      <p className="font-medium text-gray-900">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Profile Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 md:p-8">
              <h3 className="text-lg font-medium text-gray-900 mb-5 flex items-center gap-2">
                <span className="text-[#133B6E]">
                  <Key size={20} weight="duotone" />
                </span>
                Account Settings
              </h3>

              <div className="grid sm:grid-cols-2 gap-4">
                {profileOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={option.action}
                    className={`flex items-center gap-3 px-5 py-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-left ${
                      option.className
                        ? `${option.className} hover:bg-red-50`
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span
                      className={`${
                        option.className ? "text-red-600" : "text-[#133B6E]"
                      }`}
                    >
                      {option.icon}
                    </span>
                    <span className="font-medium">{option.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showConfirmLogout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 md:p-8 max-w-sm w-full shadow-xl animate-fadeIn">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 text-red-600 mx-auto mb-4">
              <SignOut size={24} weight="bold" />
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">
              Confirm Logout
            </h3>
            <p className="text-gray-600 mb-6 text-center">
              Are you sure you want to log out of your account?
            </p>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-center gap-3">
              <button
                onClick={() => setShowConfirmLogout(false)}
                className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Profile;
